import { useState, useEffect, useRef, useCallback } from "react";
import { X, Shield } from 'lucide-react';
import authService from '../services/AuthService';
import { idbLoadSet, idbSaveSet, idbLoadObject, idbSaveObject, idbDeleteKey, migrateKeysFromLocalStorage } from '../services/idbStorage';

// ─── Storage helpers ──────────────────────────────────────────────────────────
const SK_CHECKED_ASINS = "uc_checked_asins";
const SK_CHECKED_SELLERS = "uc_checked_sellers";
const SK_SEEN_BRANDS = "uc_seen_brands";
const SK_SEED_ASINS = "uc_seed_asins";   // Pool A - persistent, used to find sellers
// Pool B ("check pool") is intentionally NOT persisted - ephemeral, per-seller

export const SK_HISTORICAL_LEADS = "uc_historical_leads"; // { [asin]: leadObject } - persists across runs/sessions

export async function loadHistoricalLeads() {
    return idbLoadObject(SK_HISTORICAL_LEADS);
}
async function saveHistoricalLead(leadObj) {
    const all = await loadHistoricalLeads();
    all[leadObj.asin] = leadObj;
    await idbSaveObject(SK_HISTORICAL_LEADS, all);
}

// ─── YouTube video IDs ────────────────────────────────────────────────────────
const YT_VIDEOS = [
    "rtlDOpvow3s",
    "sFWdAhKzLuw",
    "6IAeczSBXyI",
    "Li7iGXo3Sfk",
    "Ll5H_Jh1TFM",
    "OZnUgisQLvU",
    "JWLRxRK_Acw",
];

const API_BASE = "https://server.multisellerpro.com/api";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

// ─── Crawl phases ─────────────────────────────────────────────────────────────
const PHASE = {
    IDLE: "idle",
    PICK_ASIN: "pick_asin",
    GET_SELLERS: "get_sellers",
    PICK_SELLER: "pick_seller",
    GET_ASINS: "get_asins",
    CHECK_GATING: "check_gating",
};

// ─── Animated dots ────────────────────────────────────────────────────────────
function Dots() {
    const [d, setD] = useState(1);
    useEffect(() => {
        const t = setInterval(() => setD(p => (p % 3) + 1), 500);
        return () => clearInterval(t);
    }, []);
    return <span>{".".repeat(d)}</span>;
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function UngateCrawler({ onClose, categories }) {

    // ── UI state ──────────────────────────────────────────────────────────────
    const [started, setStarted] = useState(false);
    const [running, setRunning] = useState(false);
    const [phase, setPhase] = useState(PHASE.IDLE);
    const [stats, setStats] = useState({ checked: 0, newBrands: 0 });
    const [newBrandsThisRun, setNewBrandsThisRun] = useState([]);
    const [ytIdx, setYtIdx] = useState(0);
    const [tokenCost, setTokenCost] = useState(0);
    const [foundProducts, setFoundProducts] = useState([]);
    const [showFoundProducts, setShowFoundProducts] = useState(false);
    const [showVideos, setShowVideos] = useState(false);
    const [selectedCategories, setSelectedCategories] = useState([]); // [] = all
    const [showCategoryPicker, setShowCategoryPicker] = useState(false);
    const [availableCategories, setAvailableCategories] = useState(categories);
    const [loading, setLoading] = useState(false);
    const [poolSize, setPoolSize] = useState({ seed: 0, check: 0 });

    // ── Persistence refs ──────────────────────────────────────────────────────
    const checkedAsins = useRef(new Set());
    const checkedSellers = useRef(new Set());
    const seenBrands = useRef(new Set());
    const seedPool = useRef(new Set());   // Pool A
    const checkPool = useRef([]);                        // Pool B - array, not Set

    // ── Control refs ──────────────────────────────────────────────────────────
    const stopFlag = useRef(false);
    const existingBrandsRef = useRef(new Set());
    const statsRef = useRef({ checked: 0, newBrands: 0 });
    const statusIdxRef = useRef(0);
    const tokenCostRef = useRef(0);

    // Restore persisted state on mount
    useEffect(() => {
        (async () => {
            await migrateKeysFromLocalStorage([
                SK_CHECKED_ASINS, SK_CHECKED_SELLERS, SK_SEEN_BRANDS, SK_SEED_ASINS, SK_HISTORICAL_LEADS,
            ]);
            checkedAsins.current = await idbLoadSet(SK_CHECKED_ASINS);
            checkedSellers.current = await idbLoadSet(SK_CHECKED_SELLERS);
            seenBrands.current = await idbLoadSet(SK_SEEN_BRANDS);
            seedPool.current = await idbLoadSet(SK_SEED_ASINS);

            const cached = [...seenBrands.current];
            if (cached.length) setNewBrandsThisRun(cached);
            statsRef.current.checked = checkedAsins.current.size;
            setStats({ checked: checkedAsins.current.size, newBrands: cached.length });
            setPoolSize({ seed: seedPool.current.size, check: 0 });
        })();
    }, []);

    // ── Helpers ───────────────────────────────────────────────────────────────
    const pushStats = useCallback((patch) => {
        statsRef.current = { ...statsRef.current, ...patch };
        setStats({ ...statsRef.current });
    }, []);

    const addTokens = useCallback((n) => {
        tokenCostRef.current += n;
        setTokenCost(tokenCostRef.current);
    }, []);

    const refreshPoolSize = useCallback(() => {
        setPoolSize({ seed: seedPool.current.size, check: checkPool.current.length });
    }, []);

    // ── Pick random ASIN from Pool A ──────────────────────────────────────────
    const pickRandomSeedAsin = useCallback(() => {
        const pool = [...seedPool.current];
        if (!pool.length) return null;
        return pool[Math.floor(Math.random() * pool.length)];
    }, []);

    // ── API: sellers for an ASIN ──────────────────────────────────────────────
    const getSellersForAsin = useCallback(async (asin) => {
        await sleep(3500)
        const res = await authService.authenticatedFetch(
            `${API_BASE}/amazon/sellers-for-asin/${asin}`
        );
        const data = await res.json();
        return data.sellers || [];
    }, []);

    // ── API: all ASINs for a seller ───────────────────────────────────────────
    const getAsinsForSeller = useCallback(async (sellerId) => {
        const res = await authService.authenticatedFetch(
            `${API_BASE}/user/ungated-brands/keepa-asins`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ seller_id: sellerId }),
            }
        );
        if (!res.ok) return [];
        const data = await res.json();
        return data.asins || [];
    }, []);

    // ── API: check gating for a batch ────────────────────────────────────────
    const checkGatingBatch = useCallback(async (asins) => {
        const res = await authService.authenticatedFetch(
            `${API_BASE}/a2a/check-gated-bulk`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ asins, skip_cache: true }),
            }
        );
        if (!res.ok) return {};
        const data = await res.json();
        return data.results || {};
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // Main crawl loop
    //
    // Two-pool architecture:
    //   Pool A (seedPool)  - persistent. ASINs used ONLY to pull seller lists.
    //                        Starts from brand queries. Grows when an ungated
    //                        ASIN is found (so its sellers can be explored later).
    //   Pool B (checkPool) - ephemeral. Filled fresh per seller. Checked for
    //                        gating 10 at a time, then drained. Never persisted.
    //
    // Flow:
    //   1. Pick random ASIN from Pool A → fetch its sellers
    //   2. For each seller → fill Pool B with that seller's ASINs
    //   3. Drain Pool B in chunks of 10 → check gating
    //   4. Ungated ASIN → cache brand, record if new, add to Pool A
    //   5. If seller appears to be single-brand (2 consecutive mono-brand
    //      chunks) → clear Pool B and skip to next seller
    //   6. Pool B empty → next seller. All sellers done → back to step 1
    // ─────────────────────────────────────────────────────────────────────────
    const crawl = useCallback(async (seedAsins) => {
        // Seed Pool A from initial brand query
        if (seedAsins && seedAsins.length) {
            seedAsins.forEach(a => seedPool.current.add(a));
            await idbSaveSet(SK_SEED_ASINS, seedPool.current);
            refreshPoolSize();
        }

        while (!stopFlag.current) {

            // ── Step 1: Pick random ASIN from Pool A ─────────────────────────
            setPhase(PHASE.PICK_ASIN);
            await sleep(500);
            const seedAsin = pickRandomSeedAsin();
            if (!seedAsin) {
                // Pool A is empty - re-seed from a random brand
                try {
                    const brandsRes = await authService.authenticatedFetch(`${API_BASE}/user/ungated-brands`);
                    const brandsData = await brandsRes.json();
                    // Filter brands by selected categories if any are chosen
                    if (selectedCategories.length > 0 && brandsData.brands) {
                        const catMap = brandsData.brand_categories || {};
                        brandsData.brands = brandsData.brands.filter(b => selectedCategories.includes(catMap[b]));
                    }
                    let brands = brandsData.brands || [];
                    if (!brands.length) {
                        const starterRes = await authService.authenticatedFetch(`${API_BASE}/amazon/starter-seed-brands`);
                        const starterData = await starterRes.json();
                        brands = starterData.brands || [];
                    }
                    const badRes = await authService.authenticatedFetch(`${API_BASE}/brands/my-bad-brands`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ brands }),
                    });
                    const badData = await badRes.json();
                    const badSet = new Set((badData.brands || []).map(b => b.toLowerCase()));
                    const filteredBrands = brands.filter(b => !badSet.has(b.toLowerCase()));
                    const randomBrand = [...filteredBrands].sort(() => Math.random() - 0.5)[0];
                    if (randomBrand) {
                        const r = await authService.authenticatedFetch(
                            `${API_BASE}/user/ungated-brands/keepa-asins`,
                            {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ brand_name: randomBrand, limit: 500, quality_filter: true }),
                            }
                        );
                        const d = await r.json();
                        const newAsins = (d.asins || []).slice(0, 500);
                        newAsins.forEach(a => seedPool.current.add(a));
                        await idbSaveSet(SK_SEED_ASINS, seedPool.current);
                        refreshPoolSize();
                        addTokens(10 + Math.ceil(newAsins.length / 100));
                    }
                } catch { }
                await sleep(3500);
                continue;
            }
            if (stopFlag.current) break;

            // ── Step 2: Fetch sellers for that seed ASIN ─────────────────────
            setPhase(PHASE.GET_SELLERS);
            await sleep(3500);
            let sellers = [];
            try {
                sellers = await getSellersForAsin(seedAsin);
                addTokens(sellers.length < 2 ? 12 : 3);
            } catch { continue; }
            if (stopFlag.current) break;
            if (!sellers.length) continue;

            // Shuffle so each run explores sellers in a different order
            const shuffledSellers = [...sellers].sort(() => Math.random() - 0.5);

            // ── Step 3: Loop through each seller ─────────────────────────────
            for (const sellerId of shuffledSellers) {
                if (stopFlag.current) break;
                if (checkedSellers.current.has(sellerId)) continue;

                setPhase(PHASE.PICK_SELLER);
                await sleep(500);
                if (stopFlag.current) break;

                // Fetch this seller's ASINs → fill Pool B (ephemeral)
                setPhase(PHASE.GET_ASINS);
                await sleep(4500);
                let sellerAsins = [];
                try {
                    sellerAsins = await getAsinsForSeller(sellerId);
                    addTokens(10);
                    checkedSellers.current.add(sellerId);
                    await idbSaveSet(SK_CHECKED_SELLERS, checkedSellers.current);
                } catch {
                    checkedSellers.current.add(sellerId);
                    await idbSaveSet(SK_CHECKED_SELLERS, checkedSellers.current);
                    continue;
                }
                if (stopFlag.current) break;
                if (!sellerAsins.length) continue;

                // Pool B = this seller's ASINs, shuffled, ephemeral - skip already-checked
                checkPool.current = [...sellerAsins]
                    .filter(a => !checkedAsins.current.has(a))
                    .sort(() => Math.random() - 0.5);
                refreshPoolSize();

                let singleBrandStreak = 0;
                let lastSeenBrand = null;

                // ── Step 4: Drain Pool B - check gating 10 at a time ─────────
                while (checkPool.current.length > 0 && !stopFlag.current) {
                    setPhase(PHASE.CHECK_GATING);

                    // Take first 10, mutate the array in place
                    const chunk = checkPool.current.splice(0, 10);
                    refreshPoolSize();

                    let results = {};
                    try {
                        results = await checkGatingBatch(chunk);
                    } catch { await sleep(4500); continue; }

                    // Mark these ASINs as checked
                    chunk.forEach(a => checkedAsins.current.add(a));
                    await idbSaveSet(SK_CHECKED_ASINS, checkedAsins.current);
                    pushStats({ checked: statsRef.current.checked + chunk.length });

                    // Stop if session expired
                    for (const [, status] of Object.entries(results)) {
                        if (status === "needs_login") { stopFlag.current = true; break; }
                    }
                    if (stopFlag.current) break;

                    const ungatedAsins = Object.entries(results)
                        .filter(([, s]) => s === "ungated")
                        .map(([a]) => a);

                    const chunkBrands = [];

                    // Cache brand for each ungated ASIN, record new brands, add to Pool A
                    const brandFetches = ungatedAsins.map((asin2, idx) =>
                        sleep(idx * 900)
                            .then(() => authService.authenticatedFetch(
                                `${API_BASE}/amazon/cache-ungated-brand/${asin2}`,
                                { method: "POST" }
                            ))
                            .then(r => r.json())
                            .then(async d => {
                                addTokens(1);
                                if (!d.brand) return;
                                const brand = d.brand.trim();
                                const brandKey = brand.toLowerCase();
                                const brandKeyNormalized = brand.toLowerCase().replace(/\s+/g, '');
                                chunkBrands.push(brandKeyNormalized);

                                // ── Add ungated ASIN to Pool A so we can
                                //    explore its sellers in future iterations
                                if (!seedPool.current.has(asin2)) {
                                    seedPool.current.add(asin2);
                                    await idbSaveSet(SK_SEED_ASINS, seedPool.current);
                                    refreshPoolSize();
                                }

                                // ── If interesting, add to found products
                                const bsrDrops = d.sales_rank_drops_30 || 0;
                                const verifSales = d.keepa_monthly_sold > 0 ? d.keepa_monthly_sold : 0;
                                if (bsrDrops >= 10 || verifSales > 0) {
                                    const leadObj = {
                                        asin: asin2,
                                        brand: d.brand || '',
                                        category: d.category || '',
                                        title: d.title || asin2,
                                        image: d.image || '',
                                        sales_rank: d.sales_rank || -1,
                                        sales_rank_drops_30: bsrDrops,
                                        keepa_monthly_sold: d.keepa_monthly_sold || -1,
                                        az_price: d.az_price || 0,
                                        bsr_avg30: d.bsr_avg30 || 0,
                                        opened: false,
                                        foundAt: Date.now(),
                                    };
                                    setFoundProducts(prev => {
                                        if (prev.some(p => p.asin === asin2)) return prev;
                                        return [leadObj, ...prev];
                                    });
                                    saveHistoricalLead(leadObj);
                                }

                                // Record brand if genuinely new
                                if (existingBrandsRef.current.has(brandKey)) return;
                                if (seenBrands.current.has(brandKey)) return;
                                seenBrands.current.add(brandKey);
                                await idbSaveSet(SK_SEEN_BRANDS, seenBrands.current);
                                setNewBrandsThisRun(prev => {
                                    const already = prev.some(b => b.toLowerCase() === brandKey);
                                    if (already) return prev;
                                    return [...prev, brand];
                                });
                                pushStats({ newBrands: statsRef.current.newBrands + 1 });
                            })
                            .catch(() => { })
                    );
                    if (brandFetches.length) await Promise.all(brandFetches);

                    // ── Step 5: Single-brand seller detection ─────────────────
                    // If a chunk of 5+ ungated ASINs all resolve to the same brand,
                    // track the streak. Two consecutive mono-brand chunks → seller
                    // is single-brand; clear Pool B and move to next seller.
                    if (chunkBrands.length >= 5) {
                        const uniqueBrands = new Set(chunkBrands);
                        if (uniqueBrands.size === 1) {
                            const chunkBrand = [...uniqueBrands][0];
                            if (chunkBrand === lastSeenBrand) {
                                singleBrandStreak++;
                            } else {
                                lastSeenBrand = chunkBrand;
                                singleBrandStreak = 1;
                            }
                            if (singleBrandStreak >= 2) {
                                checkPool.current = [];   // drain Pool B, skip this seller
                                refreshPoolSize();
                                singleBrandStreak = 0;
                                lastSeenBrand = null;
                                break;
                            }
                        } else {
                            singleBrandStreak = 0;
                            lastSeenBrand = null;
                        }
                    }

                    await sleep(4500);
                }
                // Pool B empty → move to next seller
            }
            // All sellers for this seed ASIN done → loop back, pick another from Pool A
        }

        if (!stopFlag.current) {
            setPhase(PHASE.IDLE);
            setRunning(false);
        }
    }, [pickRandomSeedAsin, getSellersForAsin, getAsinsForSeller, checkGatingBatch, pushStats, addTokens, refreshPoolSize, selectedCategories]);

    // ── Start ─────────────────────────────────────────────────────────────────
    const start = useCallback(async () => {
        setLoading(true);
        stopFlag.current = false;
        const brandsRes = await authService.authenticatedFetch(`${API_BASE}/user/ungated-brands`);
        const brandsData = await brandsRes.json();

        // Filter brands by selected categories if any are chosen
        if (selectedCategories.length > 0 && brandsData.brands) {
            const catMap = brandsData.brand_categories || {};
            brandsData.brands = brandsData.brands.filter(b => selectedCategories.includes(catMap[b]));
        }

        if (!brandsData.brands || brandsData.brands.length === 0) {
            const starterRes = await authService.authenticatedFetch(`${API_BASE}/amazon/starter-seed-brands`);
            const starterData = await starterRes.json();
            const starterBrand = (starterData.brands || [])[0];
            if (starterBrand) brandsData.brands = [starterBrand];
        }
        const isResume = started;
        setStarted(true);
        setRunning(true);
        if (!isResume) {
            setNewBrandsThisRun([]);
            setFoundProducts([]);
            seenBrands.current.clear();
            await idbSaveSet(SK_SEEN_BRANDS, seenBrands.current);
        }
        setPhase(PHASE.PICK_ASIN);

        // If Pool A already has ASINs from a previous run, skip seeding entirely
        if (seedPool.current.size > 0) {
            setLoading(false);
            crawl([]);
            return;
        }

        let seedAsins = [];

        try {
            const brands = brandsData.brands || [];

            // Track existing brands so we don't re-announce them as "new"
            existingBrandsRef.current = new Set(brands.map(b => b.toLowerCase().trim()));

            // Step 1: seed from DB products - free, no Keepa tokens
            const shuffledBrands = [...brands].sort(() => Math.random() - 0.5).slice(0, 5);
            for (const brand of shuffledBrands) {
                if (stopFlag.current) break;
                try {
                    const r2 = await authService.authenticatedFetch(
                        `${API_BASE}/user/ungated-brands/products`,
                        {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ brand_name: brand }),
                        }
                    );
                    const d2 = await r2.json();
                    const asins = (d2.products || []).map(p => p.asin).filter(Boolean);
                    seedAsins = [...new Set([...seedAsins, ...asins])];
                } catch { }
            }

            // Step 2: if DB gave us very few ASINs, fallback to a Keepa brand query
            if (seedAsins.length < 20) {
                const fallbackBrand = shuffledBrands[0];
                if (fallbackBrand && !stopFlag.current) {
                    try {
                        const r3 = await authService.authenticatedFetch(
                            `${API_BASE}/user/ungated-brands/keepa-asins`,
                            {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ brand_name: fallbackBrand, limit: 100 }),
                            }
                        );
                        const d3 = await r3.json();
                        const asins = (d3.asins || []).slice(0, 100);
                        addTokens(10 + Math.ceil(asins.length / 100));
                        seedAsins = [...new Set([...seedAsins, ...asins])];
                    } catch { }
                }
            }
        } catch { }

        // Merge any existing Pool A ASINs in
        const existingPool = [...seedPool.current];
        seedAsins = [...new Set([...seedAsins, ...existingPool])];

        setLoading(false);
        crawl(seedAsins);
    }, [crawl, addTokens, selectedCategories]);

    // ── Stop ──────────────────────────────────────────────────────────────────
    const stop = useCallback(() => {
        stopFlag.current = true;
        setRunning(false);
        setPhase(PHASE.IDLE);
    }, []);

    // ── Reset ─────────────────────────────────────────────────────────────────
    const reset = useCallback(async () => {
        stopFlag.current = true;
        checkedAsins.current.clear();
        checkedSellers.current.clear();
        seenBrands.current.clear();
        seedPool.current.clear();
        checkPool.current = [];
        await Promise.all(
            [SK_CHECKED_ASINS, SK_CHECKED_SELLERS, SK_SEEN_BRANDS, SK_SEED_ASINS].map(k => idbDeleteKey(k))
        );
        statsRef.current = { checked: 0, newBrands: 0 };
        tokenCostRef.current = 0;
        setStats({ checked: 0, newBrands: 0 });
        setTokenCost(0);
        setPoolSize({ seed: 0, check: 0 });
        setNewBrandsThisRun([]);
        setFoundProducts([]);
        setShowFoundProducts(false);
        setStarted(false);
        setRunning(false);
        setPhase(PHASE.IDLE);
    }, []);

    const exportFoundProducts = useCallback(() => {
        if (!foundProducts.length) return;
        const headers = ["ASIN", "Brand", "Title", "Category", "Price", "BSR", "BSR Drops/mo", "Monthly Sold", "BSR Avg30"];
        const rows = foundProducts.map(p => [
            p.asin,
            `"${(p.brand || '').replace(/"/g, '""')}"`,
            `"${(p.title || '').replace(/"/g, '""')}"`,
            `"${(p.category || '').replace(/"/g, '""')}"`,
            p.az_price > 0 ? p.az_price.toFixed(2) : '',
            p.sales_rank > 0 ? p.sales_rank : '',
            p.sales_rank_drops_30 > 0 ? p.sales_rank_drops_30 : '',
            p.keepa_monthly_sold > 0 ? p.keepa_monthly_sold : '',
            p.bsr_avg30 > 0 ? p.bsr_avg30 : '',
        ]);
        const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "ungated_products.csv"; a.click();
        URL.revokeObjectURL(url);
    }, [foundProducts]);

    const isRunning = running && phase !== PHASE.IDLE;

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: "fixed", inset: 0, zIndex: 60,
                background: "rgba(0,0,0,0.52)",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: 16,
            }}
        >
            <style>{`
                @keyframes uc-border-spin {
                    0%   { background-position: 0% 0%; }
                    100% { background-position: 300% 0%; }
                }
                @keyframes uc-pulse-num {
                    from { opacity: 0.6; transform: scale(0.97); }
                    to   { opacity: 1;   transform: scale(1); }
                }
                .uc-badge {
                    display: inline-block;
                    background: #ecfdf5;
                    color: #065f46;
                    border: 1px solid #6ee7b7;
                    font-size: 11px;
                    font-weight: 600;
                    padding: 2px 9px;
                    border-radius: 999px;
                }
            `}</style>

            <div style={{
                background: "white", borderRadius: 20, width: "100%", maxWidth: 520,
                boxShadow: "0 32px 80px rgba(0,0,0,0.22)", marginTop: "3%",
                display: "flex", flexDirection: "column", overflow: "hidden",
            }}>

                {/* ── Header ── */}
                <div style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "18px 20px 16px", borderBottom: "1px solid #f3f4f6"
                }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                        background: "linear-gradient(135deg,#059669,#34d399)",
                        display: "flex", alignItems: "center", justifyContent: "center"
                    }}>
                        <svg fill="#ffffff" version="1.1" id="Capa_1" viewBox="0 0 71.682 71.682" stroke="#ffffff"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g> <g> <path d="M48.161,33.825l0.672,0.447c-1.121,0.673-2.912,1.12-5.377,1.567V34.72c2.017-2.017,3.137-4.704,3.137-7.616h1.347v-5.376 h-4.033v5.376h1.344c0,2.465-0.896,4.479-2.24,6.271l-3.584-4.032h-7.617l-3.359,3.81c-1.344-1.568-2.24-3.81-2.24-6.272h1.344 v-5.376h-4.031v5.601h1.344c0,2.912,1.12,5.601,3.136,7.616v1.119c-2.24-0.447-4.032-0.896-5.376-1.567l0.672-0.447l-2.688-4.704 l-3.584,2.016l2.688,4.704l1.344-0.896c1.568,1.119,4.032,1.792,6.944,2.24v2.463c-1.344,1.346-2.464,3.139-2.912,5.151h-1.567 v5.375h4.031v-5.149h-0.896c0.448-1.344,1.12-2.688,2.016-3.81l3.136,3.81h7.617l3.137-3.81c0.896,1.119,1.566,2.466,2.017,3.81 H43.68v5.377h4.031v-5.377h-1.568c-0.447-2.019-1.565-3.81-2.91-5.15v-2.465c2.91-0.447,5.375-1.346,6.943-2.241l1.344,0.896 l2.688-4.702l-3.584-2.018L48.161,33.825z"></path>  </g> </g> </g></svg>
                    </div>
                    <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#111827" }}>
                            Ungate Crawler
                        </p>
                        <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>
                            Discovers new ungated brands AUTOMATICALLY
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            width: 28, height: 28, borderRadius: "50%", border: "none",
                            background: "#f3f4f6", cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "#6b7280"
                        }}
                    >
                        <X size={14} />
                    </button>
                </div>

                {/* ── Body ── */}
                <div style={{ padding: "20px 20px 4px" }}>

                    {/* Stats */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                        <div style={{ position: "relative", borderRadius: 12, padding: 2 }}>
                            {isRunning && (
                                <div style={{
                                    position: "absolute", inset: 0, borderRadius: 12,
                                    background: "linear-gradient(90deg, #d1fae5 0%, #d1fae5 30%, #10b981 50%, #d1fae5 70%, #d1fae5 100%)",
                                    backgroundSize: "300% 100%",
                                    animation: "uc-border-spin 1.6s linear infinite",
                                }} />
                            )}
                            <div style={{
                                position: "relative", zIndex: 1,
                                borderRadius: 10, padding: "14px 16px",
                                background: isRunning ? "#ecfdf5" : "#f9fafb",
                                border: isRunning ? "none" : "1px solid #f0f0f0",
                                transition: "background 0.3s",
                            }}>
                                <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 500, color: isRunning ? "#059669" : "#9ca3af" }}>
                                    ASINs checked {isRunning ? <Dots /> : ""}
                                </p>
                                <p style={{
                                    margin: 0, fontSize: 30, fontWeight: 700, lineHeight: 1,
                                    color: isRunning ? "#059669" : "#111827",
                                    animation: isRunning ? "uc-pulse-num 0.4s ease-in-out infinite alternate" : "none"
                                }}>
                                    {stats.checked.toLocaleString()}
                                </p>
                                <p style={{ margin: "4px 0 0", fontSize: 10, color: "#9ca3af", lineHeight: 1 }}>
                                    {started && !isRunning ? "⏸ Paused — press Start to resume" : poolSize.check > 0 ? `Queue: ${poolSize.check.toLocaleString()}` : ""}
                                </p>
                            </div>
                        </div>
                        <div style={{
                            background: stats.newBrands > 0 ? "#ecfdf5" : "#f9fafb",
                            borderRadius: 12, padding: "14px 16px",
                            border: `1px solid ${stats.newBrands > 0 ? "#6ee7b7" : "#f0f0f0"}`,
                            transition: "all 0.3s"
                        }}>
                            <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 500, color: stats.newBrands > 0 ? "#059669" : "#9ca3af" }}>
                                New ungated brands found
                            </p>
                            <p style={{ margin: 0, fontSize: 30, fontWeight: 700, lineHeight: 1, color: stats.newBrands > 0 ? "#059669" : "#111827" }}>
                                {stats.newBrands}
                            </p>
                        </div>
                    </div>

                    {/* New ungated brands found */}
                    {newBrandsThisRun.length > 0 && (
                        <div style={{
                            background: "#ecfdf5", borderRadius: 12, padding: "10px 12px",
                            border: "1px solid #a7f3d0", marginBottom: 14,
                            maxHeight: 80, overflowY: "auto"
                        }}>
                            <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 600, color: "#065f46" }}>
                                ✅ Your new ungated brands:
                            </p>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                {newBrandsThisRun.map(b => (
                                    <span key={b} className="uc-badge">{b}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── Found Interesting Products button ── */}
                    {foundProducts.length > 0 && (
                        <button
                            onClick={() => setShowFoundProducts(true)}
                            style={{
                                width: "100%", marginBottom: 14, padding: "11px 16px",
                                borderRadius: 10, border: "none",
                                background: "linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(13, 148, 136) 50%, rgb(37, 99, 235) 100%)",
                                cursor: "pointer",
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: 15 }}>🎯</span>
                                <span style={{ fontSize: 13, fontWeight: 700, color: "white" }}>
                                    See your ungated product leads!
                                </span>
                            </div>
                            <span style={{
                                background: "rgba(255,255,255,0.15)", color: "white", fontWeight: 700,
                                fontSize: 12, borderRadius: 999, padding: "2px 10px",
                                border: "1px solid rgba(255,255,255,0.25)"
                            }}>
                                {foundProducts.length}
                            </span>
                        </button>
                    )}

                    {/* Category filter note - no categories yet */}
                    {!started && availableCategories.length === 0 && (
                        <div style={{
                            marginBottom: 14, padding: "10px 14px", borderRadius: 10,
                            border: "1px dashed #e5e7eb", background: "#f9fafb",
                            fontSize: 12, color: "#9ca3af", textAlign: "center",
                        }}>
                            🎯 Focusing the crawler on specific categories will unlock once you find more ungated brands from different categories.
                        </div>
                    )}

                    {/* Category filter button - only before starting */}
                    {!started && availableCategories.length > 0 && (
                        <div style={{ marginBottom: 14 }}>
                            <button
                                onClick={() => setShowCategoryPicker(true)}
                                style={{
                                    width: "100%", padding: "10px 16px", borderRadius: 10,
                                    border: `1px solid ${selectedCategories.length > 0 ? "#6ee7b7" : "#e5e7eb"}`,
                                    background: selectedCategories.length > 0 ? "#ecfdf5" : "#f9fafb",
                                    cursor: "pointer", display: "flex", alignItems: "center",
                                    justifyContent: "space-between", color: "#374151",
                                    fontWeight: 600, fontSize: 13,
                                }}
                            >
                                <span>🎯 Focus on categories</span>
                                <span style={{
                                    fontSize: 11, fontWeight: 700,
                                    color: selectedCategories.length > 0 ? "#059669" : "#9ca3af",
                                    background: selectedCategories.length > 0 ? "#d1fae5" : "#f3f4f6",
                                    padding: "2px 10px", borderRadius: 999,
                                }}>
                                    {selectedCategories.length === 0
                                        ? "All categories"
                                        : `${selectedCategories.length} selected`}
                                </span>
                            </button>
                        </div>
                    )}

                    {/* YouTube - only after start */}
                    {started && (
                        <div style={{ marginBottom: 4 }}>
                            <button
                                onClick={() => setShowVideos(true)}
                                style={{
                                    width: "100%", padding: "10px 16px", borderRadius: 10,
                                    border: "1px solid #e5e7eb", background: "#f9fafb",
                                    cursor: "pointer", display: "flex", alignItems: "center",
                                    justifyContent: "center", gap: 8, color: "#374151",
                                    fontWeight: 600, fontSize: 13,
                                }}
                            >
                                <span>▶</span> Watch while you wait
                            </button>
                        </div>
                    )}
                </div>

                {/* ── Footer ── */}
                <div style={{
                    display: "flex", gap: 8, padding: "16px 20px",
                    borderTop: "1px solid #f3f4f6", marginTop: 8
                }}>
                    {!isRunning ? (
                        <button
                            onClick={start}
                            disabled={loading}
                            style={{
                                flex: 1, padding: "10px 0", borderRadius: 10, border: "none",
                                background: loading ? "#6ee7b7" : "#059669", color: "white", fontWeight: 700,
                                cursor: loading ? "default" : "pointer",
                                fontSize: 13, cursor: "pointer",
                                display: "flex", alignItems: "center", justifyContent: "center", gap: 7
                            }}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                                <polygon points="5,3 19,12 5,21" />
                            </svg>
                            {loading ? "Setting up Ungate Crawler…" : started ? "Resume Crawling" : "Start Crawling"}
                        </button>
                    ) : (
                        <button
                            onClick={stop}
                            style={{
                                flex: 1, padding: "10px 0", borderRadius: 10, border: "none",
                                background: "#ef4444", color: "white", fontWeight: 700,
                                fontSize: 13, cursor: "pointer",
                                display: "flex", alignItems: "center", justifyContent: "center", gap: 7
                            }}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                                <rect x="4" y="4" width="16" height="16" />
                            </svg>
                            Stop
                        </button>
                    )}
                    <button
                        onClick={reset}
                        style={{
                            padding: "10px 16px", borderRadius: 10,
                            border: "1px solid #e5e7eb", background: "white",
                            color: "#6b7280", fontWeight: 600, fontSize: 12, cursor: "pointer"
                        }}
                    >
                        Reset
                    </button>
                </div>

            </div>
            {/* ── Found Products Popup ── */}
            {showFoundProducts && (
                <div
                    onClick={e => { if (e.target === e.currentTarget) setShowFoundProducts(false); }}
                    style={{
                        position: "fixed", inset: 0, zIndex: 70,
                        background: "rgba(0,0,0,0.45)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 16,
                    }}
                >
                    <div style={{
                        background: "white", borderRadius: 20, width: "100%", maxWidth: 540,
                        boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
                        display: "flex", flexDirection: "column", overflow: "hidden",
                        maxHeight: "85vh",
                    }}>
                        {/* Header */}
                        <div style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "16px 20px", borderBottom: "1px solid #f3f4f6", flexShrink: 0,
                        }}>
                            <div>
                                <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#111827" }}>
                                    Interesting Ungated Products
                                </p>
                                <p style={{ margin: 0, fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                                    You're ungated - use Universal Reverse Source to find a supplier!
                                </p>
                                <button
                                    onClick={exportFoundProducts}
                                    style={{
                                        marginTop: 6, padding: "5px 12px", borderRadius: 7,
                                        border: "1px solid #a7f3d0", background: "#ecfdf5",
                                        color: "#065f46", fontWeight: 600, fontSize: 11,
                                        cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
                                    }}
                                >
                                    ⬇️ Export CSV ({foundProducts.length})
                                </button>
                            </div>
                            <button
                                onClick={() => setShowFoundProducts(false)}
                                style={{
                                    width: 28, height: 28, borderRadius: "50%", border: "none",
                                    background: "#f3f4f6", cursor: "pointer",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    color: "#6b7280", flexShrink: 0,
                                }}
                            >
                                <X size={14} />
                            </button>
                        </div>

                        {/* Product list */}
                        <div style={{ overflowY: "auto", maxHeight: "60vh", padding: "14px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
                            {foundProducts.map(p => {
                                const keepaUrl = `https://keepa.com/#!finder/${encodeURIComponent(JSON.stringify({
                                    f: { brand: { filterType: "autocomplete", filter: (p.brand || '').toLowerCase() } },
                                    s: [{ colId: "SALES_current", sort: "asc" }, { colId: "monthlySold", sort: "desc" }],
                                    t: "g"
                                }))}`;
                                return (
                                    <div key={p.asin} style={{
                                        display: "flex", alignItems: "center", gap: 10,
                                        background: p.opened ? "#f9fafb" : "white",
                                        border: `1px solid ${p.opened ? "#e5e7eb" : "#d1fae5"}`,
                                        borderRadius: 10, padding: "10px 12px",
                                        opacity: p.opened ? 0.7 : 1,
                                        transition: "all 0.2s",
                                        position: "relative",
                                    }}>
                                        <button
                                            title="Remove"
                                            onClick={() => setFoundProducts(prev => prev.filter(x => x.asin !== p.asin))}
                                            style={{
                                                position: "absolute", top: 4, left: 4,
                                                width: 16, height: 16, borderRadius: "50%",
                                                border: "none", background: "#fee2e2",
                                                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                                                color: "#ef4444", fontSize: 9, fontWeight: 700, lineHeight: 1, padding: 0,
                                            }}
                                        >✕</button>
                                        {/* Image */}
                                        {p.image
                                            ? <img src={p.image} alt="" style={{ width: 46, height: 46, objectFit: "contain", borderRadius: 8, flexShrink: 0, background: "#f3f4f6" }} />
                                            : <div style={{ width: 46, height: 46, borderRadius: 8, background: "#f3f4f6", flexShrink: 0 }} />
                                        }

                                        {/* Info */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <p style={{ margin: "0 0 3px", fontSize: 11, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {p.title || p.asin}
                                            </p>
                                            {p.brand && (
                                                <p style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 700, color: "#374151", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                                    {p.brand}
                                                    <button
                                                        title="Open brand in Keepa Finder"
                                                        onClick={() => window.open(keepaUrl, '_blank')}
                                                        style={{
                                                            display: "inline-flex", alignItems: "center", gap: 3,
                                                            padding: "1px 6px", borderRadius: 5,
                                                            border: "1px solid #e5e7eb", background: "white",
                                                            cursor: "pointer", flexShrink: 0,
                                                        }}
                                                    >
                                                        <img src="/icons/keepa.png" alt="Keepa" style={{ width: 11, height: 11, objectFit: "contain" }} />
                                                        <span style={{ fontSize: 9, fontWeight: 600, color: "#374151" }}>Brand</span>
                                                    </button>
                                                    {p.az_price > 0 && <span style={{ fontWeight: 400, fontSize: 11, color: "#6b7280" }}>${p.az_price.toFixed(2)}</span>}
                                                </p>
                                            )}
                                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                                {p.sales_rank > 0 && (
                                                    <span style={{ fontSize: 9, background: "#f3f4f6", color: "#374151", borderRadius: 4, padding: "1px 6px" }}>
                                                        BSR #{p.sales_rank.toLocaleString()}
                                                    </span>
                                                )}
                                                {p.sales_rank_drops_30 > 0 && (
                                                    <span style={{ fontSize: 9, background: "#ecfdf5", color: "#065f46", borderRadius: 4, padding: "1px 6px" }}>
                                                        📉 {p.sales_rank_drops_30} drops/mo
                                                    </span>
                                                )}
                                                {p.keepa_monthly_sold > 0 && (
                                                    <span style={{ fontSize: 9, background: "#eff6ff", color: "#1d4ed8", borderRadius: 4, padding: "1px 6px" }}>
                                                        ✅ {p.keepa_monthly_sold.toLocaleString()} sold/mo
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Action buttons */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: 5, flexShrink: 0 }}>
                                            {/* Google / Universal Reverse Scan */}
                                            <button
                                                title="Universal Reverse Scan on Google"
                                                onClick={() => {
                                                    const EXT_ID = process.env.REACT_APP_MSP_EXTENSION_ID || 'elebkaoefjdagkbfienaoonkgifogkob';
                                                    if (window.chrome?.runtime?.sendMessage && EXT_ID) {
                                                        window.chrome.runtime.sendMessage(EXT_ID, {
                                                            type: 'MSP_SEARCH_INTENT',
                                                            marketplace: 'amazon',
                                                            identifier: p.asin,
                                                            title: p.title,
                                                        });
                                                    }
                                                    if (p.title) window.open(`https://www.google.com/search?q=${encodeURIComponent(p.title)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                                    setFoundProducts(prev => prev.map(x => x.asin === p.asin ? { ...x, opened: true } : x));
                                                }}
                                                style={{
                                                    width: 30, height: 30, borderRadius: 8, padding: 0,
                                                    border: "1px solid #e5e7eb", background: "white",
                                                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                                                }}
                                            >
                                                <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="Google" style={{ width: 16, height: 16, objectFit: "contain" }} />
                                            </button>
                                            {/* Keepa — product page */}
                                            <button
                                                title="Open product on Keepa"
                                                onClick={() => window.open(`https://keepa.com/#!product/1-${p.asin}`, '_blank')}
                                                style={{
                                                    width: 30, height: 30, borderRadius: 8, padding: 0,
                                                    border: "1px solid #e5e7eb", background: "white",
                                                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                                                }}
                                            >
                                                <img src="/icons/keepa.png" alt="Keepa product" style={{ width: 16, height: 16, objectFit: "contain" }} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Category Picker Popup ── */}
            {showCategoryPicker && (
                <div
                    onClick={e => { if (e.target === e.currentTarget) setShowCategoryPicker(false); }}
                    style={{
                        position: "fixed", inset: 0, zIndex: 70,
                        background: "rgba(0,0,0,0.45)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 16,
                    }}
                >
                    <div style={{
                        background: "white", borderRadius: 20, width: "100%", maxWidth: 480,
                        boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
                        display: "flex", flexDirection: "column", overflow: "hidden",
                        maxHeight: "75vh",
                    }}>
                        {/* Header */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #f3f4f6" }}>
                            <div>
                                <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#111827" }}>Focus on Categories</p>
                                <p style={{ margin: 0, fontSize: 11, color: "#6b7280", marginTop: 2 }}>Crawler will only focus on brands in selected categories</p>
                            </div>
                            <button onClick={() => setShowCategoryPicker(false)} style={{ width: 28, height: 28, borderRadius: "50%", border: "none", background: "#f3f4f6", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}>
                                <X size={14} />
                            </button>
                        </div>

                        {/* Select All / Clear */}
                        <div style={{ padding: "10px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", gap: 8 }}>
                            <button
                                onClick={() => window.open("https://www.youtube.com/watch?v=BeU7xcAETMA", '_blank')}
                                style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid #fad1d1", background: "#fdecec", color: "#5f0606", fontWeight: 600, fontSize: 11, cursor: "pointer" }}
                            >
                                ▶︎ Watch Guide
                            </button>
                            <button
                                onClick={() => setSelectedCategories([...availableCategories])}
                                style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid #d1fae5", background: "#ecfdf5", color: "#065f46", fontWeight: 600, fontSize: 11, cursor: "pointer" }}
                            >
                                Select All
                            </button>
                            <button
                                onClick={() => setSelectedCategories([])}
                                style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid #e5e7eb", background: "white", color: "#6b7280", fontWeight: 600, fontSize: 11, cursor: "pointer" }}
                            >
                                Clear (All Categories)
                            </button>
                            <span style={{ marginLeft: "auto", fontSize: 11, color: "#9ca3af", alignSelf: "center" }}>
                                {selectedCategories.length === 0 ? "All" : selectedCategories.length} / {availableCategories.length}
                            </span>
                        </div>

                        {/* Category list */}
                        <div style={{ overflowY: "auto", padding: "12px 20px", display: "flex", flexDirection: "column", gap: 6 }}>
                            {availableCategories.map(cat => {
                                const isSelected = selectedCategories.length === 0 || selectedCategories.includes(cat);
                                const isChecked = selectedCategories.includes(cat);
                                return (
                                    <label key={cat} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, background: isChecked ? "#ecfdf5" : "#f9fafb", border: `1px solid ${isChecked ? "#6ee7b7" : "#f0f0f0"}`, cursor: "pointer", transition: "all 0.15s" }}>
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => setSelectedCategories(prev =>
                                                prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
                                            )}
                                            style={{ accentColor: "#059669", width: 15, height: 15, flexShrink: 0 }}
                                        />
                                        <span style={{ fontSize: 13, fontWeight: 500, color: "#111827" }}>{cat}</span>
                                    </label>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div style={{ padding: "14px 20px", borderTop: "1px solid #f3f4f6" }}>
                            <button
                                onClick={() => setShowCategoryPicker(false)}
                                style={{ width: "100%", padding: "10px", borderRadius: 10, border: "none", background: "#059669", color: "white", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                            >
                                Apply — {selectedCategories.length === 0 ? "Crawl All Categories" : `Focus on ${selectedCategories.length} Categor${selectedCategories.length === 1 ? "y" : "ies"}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Videos Popup ── */}
            {showVideos && (
                <div
                    onClick={e => { if (e.target === e.currentTarget) setShowVideos(false); }}
                    style={{
                        position: "fixed", inset: 0, zIndex: 70,
                        background: "rgba(0,0,0,0.45)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 16,
                    }}
                >
                    <div style={{
                        background: "white", borderRadius: 20, width: "100%", maxWidth: 820,
                        boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
                        display: "flex", flexDirection: "column", overflow: "hidden",
                    }}>
                        <div style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "16px 20px", borderBottom: "1px solid #f3f4f6",
                        }}>
                            <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#111827" }}>Watch while you wait</p>
                            <button
                                onClick={() => setShowVideos(false)}
                                style={{
                                    width: 28, height: 28, borderRadius: "50%", border: "none",
                                    background: "#f3f4f6", cursor: "pointer",
                                    display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280",
                                }}
                            ><X size={14} /></button>
                        </div>
                        <div style={{ padding: "16px 20px 20px" }}>
                            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                                <button
                                    onClick={() => setYtIdx(i => (i - 1 + YT_VIDEOS.length) % YT_VIDEOS.length)}
                                    style={{
                                        position: "absolute", left: -17, zIndex: 2,
                                        width: 34, height: 34, borderRadius: "50%", border: "1.5px solid black",
                                        background: "white", boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
                                        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#374151",
                                    }}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                                </button>
                                <div style={{
                                    flex: 1, borderRadius: 12, overflow: "hidden",
                                    border: "1px solid #e5e7eb", background: "#000", aspectRatio: "16/9",
                                }}>
                                    <iframe
                                        key={ytIdx}
                                        width="100%" height="100%"
                                        src={`https://www.youtube.com/embed/${YT_VIDEOS[ytIdx]}?controls=1`}
                                        allow="autoplay; encrypted-media"
                                        allowFullScreen
                                        style={{ display: "block", border: "none" }}
                                    />
                                </div>
                                <button
                                    onClick={() => setYtIdx(i => (i + 1) % YT_VIDEOS.length)}
                                    style={{
                                        position: "absolute", right: -17, zIndex: 2,
                                        width: 34, height: 34, borderRadius: "50%", border: "1.5px solid black",
                                        background: "white", boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
                                        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#374151",
                                    }}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                                </button>
                            </div>
                            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 8 }}>
                                {YT_VIDEOS.map((_, i) => (
                                    <button key={i} onClick={() => setYtIdx(i)} style={{
                                        width: 7, height: 7, borderRadius: "50%", border: "none",
                                        cursor: "pointer", padding: 0,
                                        background: i === ytIdx ? "#059669" : "#d1d5db",
                                    }} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}